import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, ArrowRight, Swords, Trophy, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WagerCard } from '@/components/wagers/WagerCard';
import { GameIcon } from '@/components/ui/GameIcon';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export function DashboardPage() {
  const { data: wagers, isLoading } = useQuery({
    queryKey: ['my-wagers'],
    queryFn: () => api.getMyWagers('open,agreed,funding,locked,in_progress,pending_verification,ready_to_collect'),
  });

  const { data: recentWagers } = useQuery({
    queryKey: ['my-wagers-recent'],
    queryFn: () => api.getMyWagers('settled,voided'),
  });

  const activeWagers = wagers ?? [];
  const recentResults = recentWagers?.slice(0, 3) ?? [];

  const stats = {
    activeWagers: activeWagers.length,
    totalWagered: 0,
    winRate: 65,
  };

  return (
    <div className="space-y-8 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's your challenge overview.</p>
        </div>
        <Link to="/wagers/create">
          <Button variant="gradient" className="gap-2">
            <Plus className="h-4 w-4" />
            New Challenge
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-primary/10 p-3">
                <Swords className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Challenges</p>
                <p className="font-heading text-2xl font-bold">{stats.activeWagers}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-green-500/10 p-3">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Staked</p>
                <p className="font-heading text-2xl font-bold">
                  {formatCurrency(stats.totalWagered)}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-yellow-500/10 p-3">
                <Trophy className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Win Rate</p>
                <p className="font-heading text-2xl font-bold">{stats.winRate}%</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Active Challenges */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Active Challenges</CardTitle>
          <Link to="/wagers" className="text-sm text-primary hover:underline">
            View all <ArrowRight className="ml-1 inline h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : activeWagers && activeWagers.length > 0 ? (
            <div className="space-y-4">
              {activeWagers.slice(0, 3).map((wager) => (
                <WagerCard key={wager.id} wager={wager} />
              ))}
            </div>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center text-center">
              <Swords className="mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-muted-foreground">No active challenges</p>
              <Link to="/wagers/create">
                <Button variant="link" className="mt-2">
                  Create your first challenge
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Results */}
      {recentResults.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Results</CardTitle>
            <Link to="/wagers?status=settled" className="text-sm text-primary hover:underline">
              View all <ArrowRight className="ml-1 inline h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentResults.map((wager) => (
                <WagerCard key={wager.id} wager={wager} variant="compact" />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="group cursor-pointer transition-colors hover:border-primary/50">
          <Link to="/wagers">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-blue-500/10 p-3 transition-transform group-hover:scale-110">
                <Swords className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <h3 className="font-semibold">Browse Open Challenges</h3>
                <p className="text-sm text-muted-foreground">
                  Find opponents looking for matches
                </p>
              </div>
              <ArrowRight className="ml-auto h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </CardContent>
          </Link>
        </Card>

        <Card className="group cursor-pointer transition-colors hover:border-primary/50">
          <Link to="/lobbies/chess_com">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="rounded-xl bg-purple-500/10 p-3 transition-transform group-hover:scale-110">
                <GameIcon gameCode="chess_com" size="md" className="bg-transparent" />
              </div>
              <div>
                <h3 className="font-semibold">Chess.com Arena</h3>
                <p className="text-sm text-muted-foreground">
                  See who's online and ready to play
                </p>
              </div>
              <ArrowRight className="ml-auto h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </CardContent>
          </Link>
        </Card>
      </div>
    </div>
  );
}
